import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { fmtL, fmtDate, apiErr } from "@/lib/format";
import { normalizar } from "@/components/LiveSearch";
import {
  ArrowLeftIcon, MagnifyingGlassIcon, UploadSimpleIcon, CameraIcon, FileIcon, CheckCircleIcon, WarningCircleIcon,
  ArrowClockwiseIcon, TrashIcon, FloppyDiskIcon, XIcon,
} from "@phosphor-icons/react";

const FORMATOS = "PDF, imágenes (JPG, PNG, WEBP, HEIC), Word, Excel y PowerPoint · máx. 10 MB por archivo · hasta 10 archivos";
const ACCEPT = ".pdf,.jpg,.jpeg,.png,.webp,.heic,.heif,.doc,.docx,.xls,.xlsx,.ppt,.pptx,image/*";
const MAX = 10 * 1024 * 1024;
const kb = (n) => (n >= 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);
const hoy = () => new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 10);

const Campo = ({ label, req, ayuda, children, htmlFor }) => (
  <div>
    <label htmlFor={htmlFor} className="block text-xs font-semibold text-text-secondary mb-1.5">{label}{req && <span className="text-red-300"> *</span>}</label>
    {children}
    {ayuda && <p className="text-[11px] text-text-muted mt-1">{ayuda}</p>}
  </div>
);
const inp = "w-full min-h-[44px] bg-app border border-border rounded-md px-3 py-2 text-sm";

/** Crear promesa de pago: pantalla completa (sin ventanas sobre ventanas), una columna en el móvil. */
export default function PromesaNueva() {
  const navigate = useNavigate();
  const [clientes, setClientes] = useState(null);
  const [q, setQ] = useState("");
  const [cliente, setCliente] = useState(null);
  const [info, setInfo] = useState(null);                 // saldo y facturas pendientes
  const [form, setForm] = useState({ fecha: "", nota: "", monto: "", moneda: "HNL", factura_ids: [] });
  const [archivos, setArchivos] = useState([]);           // {key, file, estado, progreso, id, error}
  const [arrastrando, setArrastrando] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [errores, setErrores] = useState({});
  const [clave] = useState(() => (window.crypto?.randomUUID ? window.crypto.randomUUID() : `${Date.now()}-${Math.random()}`));
  const inputArchivos = useRef(null);
  const inputCamara = useRef(null);

  useEffect(() => { api.get("/clients").then(({ data }) => setClientes(Array.isArray(data) ? data : data.items || [])).catch(() => setClientes([])); }, []);
  useEffect(() => {
    setInfo(null);
    if (!cliente) return;
    api.get(`/promesas-pago/cliente/${cliente.id}/facturas`).then(({ data }) => setInfo(data)).catch((e) => toast.error(apiErr(e)));
  }, [cliente]);

  const coincidencias = useMemo(() => {
    const ws = normalizar(q).split(/\s+/).filter(Boolean);
    if (!ws.length || !clientes) return [];
    return clientes.filter((c) => { const t = normalizar(`${c.nombre} ${c.codigo_cliente || ""} ${c.rtn || ""} ${c.identidad || ""}`); return ws.every((w) => t.includes(w)); }).slice(0, 8);
  }, [q, clientes]);

  // ---- adjuntos: cada uno con nombre, tamaño, avance y resultado; un fallo no borra lo escrito
  const subir = async (item) => {
    setArchivos((as) => as.map((a) => (a.key === item.key ? { ...a, estado: "subiendo", progreso: 0, error: "" } : a)));
    const fd = new FormData();
    fd.append("cliente_id", cliente.id);
    fd.append("archivo", item.file);
    try {
      const { data } = await api.post("/promesas-pago/files", fd, {
        onUploadProgress: (e) => setArchivos((as) => as.map((a) => (a.key === item.key ? { ...a, progreso: e.total ? Math.round((e.loaded / e.total) * 100) : 50 } : a))),
      });
      setArchivos((as) => as.map((a) => (a.key === item.key ? { ...a, estado: "listo", progreso: 100, id: data.id } : a)));
    } catch (e) {
      setArchivos((as) => as.map((a) => (a.key === item.key ? { ...a, estado: "error", error: apiErr(e) || "No se pudo subir" } : a)));
    }
  };
  const agregar = (files) => {
    if (!cliente) { toast.error("Primero elige el cliente"); return; }
    const nuevos = [];
    for (const file of Array.from(files || [])) {
      if (archivos.length + nuevos.length >= 10) { toast.error("Máximo 10 archivos por promesa"); break; }
      const item = { key: `${file.name}-${file.size}-${Math.random()}`, file, estado: "pendiente", progreso: 0, id: null, error: "" };
      if (file.size > MAX) { item.estado = "error"; item.error = "Supera 10 MB"; }
      nuevos.push(item);
    }
    setArchivos((as) => [...as, ...nuevos]);
    nuevos.filter((x) => x.estado === "pendiente").forEach(subir);
  };

  const validar = () => {
    const e = {};
    if (!cliente) e.cliente = "Elige el cliente.";
    if (!form.fecha) e.fecha = "Indica la fecha comprometida de pago.";
    if (form.nota.trim().length < 3) e.nota = "Escribe el motivo o compromiso acordado con el cliente.";
    if (form.monto !== "" && Number(form.monto) < 0) e.monto = "Monto inválido.";
    setErrores(e);
    return !Object.keys(e).length;
  };
  const guardar = async (ev) => {
    ev.preventDefault();
    if (guardando || !validar()) return;
    if (archivos.some((a) => a.estado === "subiendo")) { toast.error("Espera a que terminen de subir los archivos"); return; }
    if (archivos.some((a) => a.estado === "error")) { toast.error("Hay archivos que no se subieron: reinténtalos o quítalos"); return; }
    setGuardando(true);
    try {
      const { data } = await api.post("/promesas-pago", {
        cliente_id: cliente.id, fecha: form.fecha, nota: form.nota, moneda: form.moneda,
        monto: form.monto === "" ? null : Number(form.monto), factura_ids: form.factura_ids,
        adjuntos: archivos.filter((a) => a.id).map((a) => a.id), idempotency_key: clave,
      });
      toast.success("Promesa de pago guardada · aparece en el Calendario");
      navigate(`/ventas/promesas/${data.id}`, { replace: true });
    } catch (e) {
      toast.error(apiErr(e) || "No se pudo guardar; tus datos siguen en el formulario. Intenta de nuevo.");
    } finally { setGuardando(false); }
  };
  const toggleFactura = (id) => setForm((f) => ({ ...f, factura_ids: f.factura_ids.includes(id) ? f.factura_ids.filter((x) => x !== id) : [...f.factura_ids, id] }));
  const sumaSel = (info?.facturas || []).filter((f) => form.factura_ids.includes(f.id)).reduce((s, f) => s + Number(f.saldo_pendiente || 0), 0);

  return (
    <Layout title="Crear promesa de pago" subtitle="Compromiso de pago acordado con el cliente · no registra pagos ni cambia saldos">
      <form onSubmit={guardar} noValidate className="max-w-3xl space-y-5 pb-4" data-testid="promesa-form">
        <button type="button" onClick={() => navigate("/ventas?tab=promesas")} className="min-h-[44px] text-sm text-text-secondary hover:text-text-primary flex items-center gap-1.5">
          <ArrowLeftIcon size={16} /> Volver a Promesas de pago
        </button>

        <section className="bg-card border border-border/60 rounded-xl p-4 sm:p-5 space-y-4">
          <Campo label="Cliente" req htmlFor="promesa-cliente">
            {cliente ? (
              <div className="flex items-center justify-between gap-3 bg-agro-green/10 border border-agro-green/40 rounded-md px-3 py-2 min-h-[44px]" data-testid="promesa-cliente-elegido">
                <div className="min-w-0"><div className="font-semibold text-sm truncate">{cliente.nombre}</div>
                  <div className="text-[11px] text-text-muted">{cliente.codigo_cliente || ""}{cliente.rtn ? ` · RTN ${cliente.rtn}` : ""}</div></div>
                <button type="button" onClick={() => { setCliente(null); setForm((f) => ({ ...f, factura_ids: [] })); setArchivos([]); }} className="min-h-[36px] px-3 text-xs border border-border rounded-md">Cambiar</button>
              </div>
            ) : (
              <>
                <div className="relative">
                  <MagnifyingGlassIcon size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" aria-hidden="true" />
                  <input id="promesa-cliente" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Nombre, código o RTN"
                    aria-invalid={Boolean(errores.cliente)} data-testid="promesa-cliente-buscar" autoComplete="off" className={`${inp} pl-9`} />
                </div>
                {clientes === null ? <p className="text-xs text-text-muted mt-1">Cargando clientes…</p>
                  : q.trim() && coincidencias.length === 0 ? <p className="text-xs text-text-muted mt-1">Sin clientes que coincidan.</p> : null}
                {coincidencias.length > 0 && (
                  <ul className="mt-2 border border-border rounded-md divide-y divide-border/60 overflow-hidden" data-testid="promesa-cliente-resultados">
                    {coincidencias.map((c) => (
                      <li key={c.id}><button type="button" onClick={() => { setCliente(c); setQ(""); }} data-testid={`promesa-cliente-${c.id}`}
                        className="w-full min-h-[44px] text-left px-3 py-2 hover:bg-white/[0.04]">
                        <span className="text-sm font-medium">{c.nombre}</span>
                        <span className="block text-[11px] text-text-muted">{c.codigo_cliente || ""}{c.region ? ` · ${c.region}` : ""}</span>
                      </button></li>
                    ))}
                  </ul>
                )}
              </>
            )}
            {errores.cliente && <p className="text-xs text-red-300 mt-1 flex items-center gap-1"><WarningCircleIcon size={13} /> {errores.cliente}</p>}
          </Campo>

          {cliente && (
            <div className="bg-app/60 border border-border rounded-md p-3 text-sm" data-testid="promesa-saldo">
              {!info ? <span className="text-text-muted">Cargando saldo…</span> : <>
                <div className="flex flex-wrap justify-between gap-2"><span className="text-text-secondary">Saldo pendiente del cliente</span><b className="text-agro-yellow">{fmtL(info.saldo_total)}</b></div>
                {info.facturas.length === 0 ? <p className="text-xs text-text-muted mt-2">El cliente no tiene facturas pendientes.</p> : (
                  <fieldset className="mt-3">
                    <legend className="text-xs font-semibold text-text-secondary mb-1.5">Facturas relacionadas (recomendado)</legend>
                    <div className="space-y-1 max-h-60 overflow-y-auto">
                      {info.facturas.map((f) => (
                        <label key={f.id} className="flex items-center gap-3 min-h-[44px] px-2 rounded hover:bg-white/[0.03] cursor-pointer">
                          <input type="checkbox" checked={form.factura_ids.includes(f.id)} onChange={() => toggleFactura(f.id)} className="w-5 h-5" data-testid={`promesa-factura-${f.id}`} />
                          <span className="flex-1 text-xs"><b className="font-mono">{f.numero}</b> · vence {fmtDate(f.fecha_vencimiento)}</span>
                          <span className="text-xs font-semibold">{fmtL(f.saldo_pendiente)}</span>
                        </label>
                      ))}
                    </div>
                    {form.factura_ids.length > 0 && <p className="text-xs text-text-secondary mt-1">Seleccionadas: {form.factura_ids.length} · saldo {fmtL(sumaSel)}</p>}
                  </fieldset>
                )}
              </>}
            </div>
          )}
        </section>

        <section className="bg-card border border-border/60 rounded-xl p-4 sm:p-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Campo label="Fecha comprometida de pago" req htmlFor="promesa-fecha">
            <input id="promesa-fecha" type="date" min={hoy()} value={form.fecha} onChange={(e) => setForm({ ...form, fecha: e.target.value })}
              aria-invalid={Boolean(errores.fecha)} data-testid="promesa-fecha" className={inp} />
            {errores.fecha && <p className="text-xs text-red-300 mt-1 flex items-center gap-1"><WarningCircleIcon size={13} /> {errores.fecha}</p>}
          </Campo>
          <div className="grid grid-cols-[1fr_auto] gap-2">
            <Campo label="Monto comprometido (recomendado)" htmlFor="promesa-monto">
              <input id="promesa-monto" type="number" inputMode="decimal" min="0" step="0.01" value={form.monto}
                onChange={(e) => setForm({ ...form, monto: e.target.value })} aria-invalid={Boolean(errores.monto)} data-testid="promesa-monto" className={inp}
                placeholder={sumaSel ? sumaSel.toFixed(2) : "0.00"} />
            </Campo>
            <Campo label="Moneda" htmlFor="promesa-moneda">
              <select id="promesa-moneda" value={form.moneda} onChange={(e) => setForm({ ...form, moneda: e.target.value })} className={inp}>
                <option value="HNL">L (HNL)</option><option value="USD">US$ (USD)</option>
              </select>
            </Campo>
          </div>
          <div className="sm:col-span-2">
            <Campo label="Motivo o compromiso" req htmlFor="promesa-nota" ayuda="Lo acordado con el cliente: cómo y cuándo pagará.">
              <textarea id="promesa-nota" rows={4} value={form.nota} onChange={(e) => setForm({ ...form, nota: e.target.value })}
                aria-invalid={Boolean(errores.nota)} data-testid="promesa-nota" className={`${inp} min-h-[110px]`} />
              {errores.nota && <p className="text-xs text-red-300 mt-1 flex items-center gap-1"><WarningCircleIcon size={13} /> {errores.nota}</p>}
            </Campo>
          </div>
        </section>

        <section className="bg-card border border-border/60 rounded-xl p-4 sm:p-5 space-y-3">
          <div>
            <h3 className="text-sm font-semibold">Archivos adjuntos</h3>
            <p className="text-[11px] text-text-muted">{FORMATOS}. Son privados: solo los ve quien puede ver la promesa.</p>
          </div>
          <div onDragOver={(e) => { e.preventDefault(); setArrastrando(true); }} onDragLeave={() => setArrastrando(false)}
            onDrop={(e) => { e.preventDefault(); setArrastrando(false); agregar(e.dataTransfer.files); }}
            className={`border-2 border-dashed rounded-xl p-5 text-center text-sm ${arrastrando ? "border-focus bg-agro-green/5" : "border-input"} ${!cliente ? "opacity-60" : ""}`}
            data-testid="promesa-zona-archivos">
            <p className="text-text-secondary">Arrastra los archivos aquí, o</p>
            <div className="mt-3 flex flex-wrap justify-center gap-2">
              <button type="button" disabled={!cliente} onClick={() => inputArchivos.current?.click()} className="min-h-[44px] px-4 rounded-md border border-border text-sm flex items-center gap-2">
                <UploadSimpleIcon size={16} /> Seleccionar archivos</button>
              <button type="button" disabled={!cliente} onClick={() => inputCamara.current?.click()} className="min-h-[44px] px-4 rounded-md border border-border text-sm flex items-center gap-2">
                <CameraIcon size={16} /> Tomar fotografía</button>
            </div>
            {!cliente && <p className="text-[11px] text-text-muted mt-2">Elige primero el cliente.</p>}
            <input ref={inputArchivos} type="file" multiple accept={ACCEPT} className="hidden" data-testid="promesa-archivos" onChange={(e) => { agregar(e.target.files); e.target.value = ""; }} />
            <input ref={inputCamara} type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => { agregar(e.target.files); e.target.value = ""; }} />
          </div>
          {archivos.length > 0 && (
            <ul className="space-y-2" data-testid="promesa-lista-archivos">
              {archivos.map((a) => (
                <li key={a.key} className="flex items-center gap-3 border border-border rounded-md p-2" data-testid={`promesa-archivo-${a.estado}`}>
                  <FileIcon size={20} className="text-text-muted shrink-0" aria-hidden="true" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm truncate">{a.file.name}</div>
                    <div className="text-[11px] text-text-muted">{kb(a.file.size)} · {a.estado === "listo" ? <span className="text-agro-green inline-flex items-center gap-1"><CheckCircleIcon size={12} /> Subido</span>
                      : a.estado === "error" ? <span className="text-red-300 inline-flex items-center gap-1"><WarningCircleIcon size={12} /> {a.error}</span>
                      : `Subiendo ${a.progreso}%`}</div>
                    {a.estado === "subiendo" && <div className="h-1 bg-app rounded mt-1" role="progressbar" aria-valuenow={a.progreso} aria-valuemin={0} aria-valuemax={100}><div className="h-1 bg-agro-green rounded" style={{ width: `${a.progreso}%` }} /></div>}
                  </div>
                  {a.estado === "error" && a.file.size <= MAX && (
                    <button type="button" onClick={() => subir(a)} aria-label={`Reintentar ${a.file.name}`} className="min-h-[44px] min-w-[44px] flex items-center justify-center text-agro-yellow"><ArrowClockwiseIcon size={18} /></button>)}
                  <button type="button" onClick={() => setArchivos((as) => as.filter((x) => x.key !== a.key))} aria-label={`Quitar ${a.file.name}`}
                    className="min-h-[44px] min-w-[44px] flex items-center justify-center text-text-muted hover:text-red-300"><TrashIcon size={18} /></button>
                </li>
              ))}
            </ul>
          )}
        </section>

        <div className="sticky bottom-0 z-30 -mx-1 bg-card/95 backdrop-blur border border-border rounded-xl p-3 flex flex-wrap justify-end gap-2 shadow-lg">
          <button type="button" onClick={() => navigate("/ventas?tab=promesas")} className="min-h-[44px] px-4 border border-border rounded-md text-sm flex items-center gap-2"><XIcon size={16} /> Cancelar</button>
          <button type="submit" disabled={guardando} data-testid="promesa-guardar"
            className="min-h-[44px] px-5 bg-agro-green hover:bg-agro-green-hover text-black font-semibold rounded-md text-sm flex items-center gap-2 disabled:opacity-60">
            <FloppyDiskIcon size={16} weight="bold" /> {guardando ? "Guardando…" : "Guardar promesa"}
          </button>
        </div>
      </form>
    </Layout>
  );
}
